import React, { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DeviceTaskType as ChannelTaskType } from 'lavva.exalushome/build/js/Services/Devices/IDevice';
import { IDeviceChannel } from 'lavva.exalushome/build/js/Services/Devices/IDeviceChannel';
import { RgbaColor } from '@uiw/color-convert';
import ColorComponent from '../../../../../../components/channel-details-controls/light/color';
import RgbPicker from '../../../../../../components/channel-details-controls/light/picker';
import TemperatureComponent from '../../../../../../components/channel-details-controls/light/temperature';
import LavvaDetailsWrapper from '../../../../../../components/details-wrapper';
import { PICKER_COLOR_LIST } from '../../../../../../const';
import { rgbaToRgbString } from '../../../../../../utils/helpers';
import { canChannelDo } from '../../../../utils';
import { useRgbControl } from '../../hooks/use-rgb-control';
import { OnOffControls } from '../switch/components/on-off-controls';

type RgbDetailsControlsProps = {
  channel: IDeviceChannel;
  active: boolean;
};

const RgbDetailsControls: React.FC<RgbDetailsControlsProps> = ({ channel, active }) => {
  const { t } = useTranslation('channel-details');
  const [initialized, setInitialized] = useState<boolean>(false);
  const [rgbValue, setRgbValue] = useState<string>('');
  const [brightness, setBrightness] = useState<number>(100);
  const [temperature, setTemperature] = useState<number>(0);
  const [preventSwipeViews, setPreventSwipeViews] = useState<boolean>(false);
  const { state, isOn, handleControlRgb, handleControlBrightness } = useRgbControl(channel);
  const tabs = [{ label: t('rgb') }, { label: t('color') }, { label: t('white') }];

  useEffect(() => {
    if (state && !initialized) {
      setInitialized(true);
      setBrightness(state.Brightness);
      const rgb = `${state.R},${state.G},${state.B}`;
      setRgbValue(rgb);

      if (state.R === 255 && state.G === 255 && state.B === 255) setTemperature(45);
      if (state.R === 255 && state.G === 255 && state.B !== 255) {
        setTemperature(Math.round((38 * state.B) / 255));
      }
      if (state.R !== 255 && state.G === 255 && state.B === 255) {
        setTemperature(Math.round((54 * 255) / state.R));
      }
    }
  }, [state?.Brightness, state?.R, state?.G, state?.B, initialized]);

  useEffect(() => {
    const timeout = setTimeout(() => setPreventSwipeViews(false), 950);
    return () => clearTimeout(timeout);
  }, [rgbValue]);

  const handleBrightness = (brightness: number) => {
    setBrightness(brightness);
    handleControlBrightness(brightness);
  };

  const handleTemperature = (event: React.PointerEvent<HTMLInputElement>) => {
    const value = Number((event.target as HTMLInputElement).value);
    setTemperature(value);
    let rgb: string;

    if (value <= 38) rgb = `255,255,${Math.round((value * 255) / 38)}`;
    else if (value > 38 && value < 54) rgb = '255,255,255';
    else rgb = `${Math.round((54 * 255) / value)},255,255`;

    setRgbValue(rgb);
    handleControlRgb(rgb, brightness);
  };

  const handleChange = (val: number) => {
    setPreventSwipeViews(true);
    setBrightness(val);
  };

  const onChangeTemperature = (event: ChangeEvent<HTMLInputElement>) => {
    const value = Number(event.target.value);
    setTemperature(value);
    let rgb: string;

    if (value <= 38) {
      rgb = `255,255,${Math.round((value * 255) / 38)}`;
    } else if (value > 38 && value < 54) {
      rgb = '255,255,255';
    } else {
      rgb = `${Math.round((54 * 255) / value)},255,255`;
    }

    setRgbValue(rgb);
  };

  const handleColorPicked = (rgba: RgbaColor) => {
    const rgb = rgbaToRgbString(rgba);

    setRgbValue(rgb);
    handleControlRgb(rgb, brightness);
  };

  const showLightSlider = useMemo(() => canChannelDo(channel, [ChannelTaskType.SetLightBrightness]), [channel.States]);

  return (
    <LavvaDetailsWrapper
      preventSwipeEvent={preventSwipeViews}
      tabs={tabs}
      additionalTabs={[
        <ColorComponent
          key={2}
          rgbValue={rgbValue}
          handleChange={handleChange}
          brightness={brightness}
          handleBrightness={handleBrightness}
          handleControlRgb={handleControlRgb}
          colors={PICKER_COLOR_LIST}
          darkBorder
        />,
        <TemperatureComponent
          key={3}
          temperature={temperature}
          onChangeTemperature={onChangeTemperature}
          handleChange={handleChange}
          brightness={brightness}
          handleBrightness={handleBrightness}
          handleTemperature={handleTemperature}
        />,
      ]}
    >
      <RgbPicker
        rgbValue={rgbValue}
        handleChange={handleChange}
        handleColorPicked={handleColorPicked}
        handleBrightness={handleBrightness}
        isOn={isOn}
        setPreventSwipeViews={setPreventSwipeViews}
        {...(showLightSlider ? { brightness } : {})}
      >
        <OnOffControls channel={channel} active={active} />
      </RgbPicker>
    </LavvaDetailsWrapper>
  );
};

export default RgbDetailsControls;
